/**
 * PlatformTabs Component
 * Tab-based platform selection showing only connected platforms with alerts badge
 */

import React, { useState } from 'react';
import { Plus, Facebook, Search, Music, ShoppingBag } from 'lucide-react';
import { useCommandCenter } from '../../contexts/CommandCenterContext';

const PlatformTabs = ({ onConnectPlatform }) => {
    const {
        connectedPlatforms,
        selectedPlatform,
        changePlatform,
        platformAlertCounts
    } = useCommandCenter();

    const getPlatformConfig = (platform) => {
        const configs = {
            meta: {
                name: 'Meta',
                icon: '📘',
                color: '#1877F2',
                bgColor: 'rgba(24, 119, 242, 0.1)'
            },
            google: {
                name: 'Google',
                icon: '🔍',
                color: '#4285F4',
                bgColor: 'rgba(66, 133, 244, 0.1)'
            },
            tiktok: {
                name: 'TikTok',
                icon: '🎵',
                color: '#000000',
                bgColor: 'rgba(0, 0, 0, 0.1)'
            },
            amazon: {
                name: 'Amazon',
                icon: '📦',
                color: '#FF9900',
                bgColor: 'rgba(255, 153, 0, 0.1)'
            },
            linkedin: {
                name: 'LinkedIn',
                icon: '💼',
                color: '#0A66C2',
                bgColor: 'rgba(10, 102, 194, 0.1)'
            },
            twitter: {
                name: 'X/Twitter',
                icon: '𝕏',
                color: '#000000',
                bgColor: 'rgba(0, 0, 0, 0.1)'
            },
            pinterest: {
                name: 'Pinterest',
                icon: '📌',
                color: '#E60023',
                bgColor: 'rgba(230, 0, 35, 0.1)'
            },
            snapchat: {
                name: 'Snapchat',
                icon: '👻',
                color: '#FFFC00',
                bgColor: 'rgba(255, 252, 0, 0.1)'
            }
        };

        const key = platform?.toLowerCase();
        return configs[key] || {
            name: platform,
            icon: '📊',
            color: 'var(--color-primary)',
            bgColor: 'var(--color-primary-alpha)'
        };
    };

    if (connectedPlatforms.length === 0) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)',
                marginBottom: 'var(--space-4)'
            }}>
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    No platforms connected
                </span>
                <button
                    onClick={onConnectPlatform}
                    className="btn btn-primary"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-sm)'
                    }}
                >
                    <Plus size={16} />
                    Connect Platform
                </button>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-4)',
            borderBottom: '1px solid var(--color-border)',
            paddingBottom: 'var(--space-1)'
        }}>
            {connectedPlatforms.map((platform) => {
                const config = getPlatformConfig(platform.platform);
                const isSelected = selectedPlatform?.toLowerCase() === platform.platform?.toLowerCase();
                const alertCount = platform.alertCount || 0;

                return (
                    <button
                        key={platform.platform}
                        onClick={() => changePlatform(platform.platform)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: isSelected ? config.bgColor : 'transparent',
                            border: 'none',
                            borderBottom: isSelected
                                ? `2px solid ${config.color}`
                                : '2px solid transparent',
                            borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                            cursor: 'pointer',
                            transition: 'all var(--transition-fast)',
                            marginBottom: '-1px'
                        }}
                        onMouseEnter={(e) => {
                            if (!isSelected) {
                                e.currentTarget.style.background = 'var(--color-surface-hover)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSelected) {
                                e.currentTarget.style.background = 'transparent';
                            }
                        }}
                    >
                        <span style={{ fontSize: '1.1em' }}>{config.icon}</span>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? config.color : 'var(--color-text-secondary)'
                        }}>
                            {config.name}
                        </span>
                        {alertCount > 0 && (
                            <span style={{
                                background: 'var(--color-error)',
                                color: 'white',
                                padding: '1px 6px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600,
                                minWidth: '18px',
                                textAlign: 'center'
                            }}>
                                {alertCount}
                            </span>
                        )}
                    </button>
                );
            })}

            {/* Add Platform Button */}
            <button
                onClick={onConnectPlatform}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    background: 'transparent',
                    border: '1px dashed var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    color: 'var(--color-text-tertiary)',
                    transition: 'all var(--transition-fast)',
                    marginLeft: 'var(--space-2)'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.color = 'var(--color-primary)';
                    e.currentTarget.style.background = 'var(--color-primary-alpha)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.color = 'var(--color-text-tertiary)';
                    e.currentTarget.style.background = 'transparent';
                }}
                title="Connect another platform"
            >
                <Plus size={18} />
            </button>
        </div>
    );
};

export default PlatformTabs;
